import { useSuspenseQuery, useLazyQuery } from "@apollo/client";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  Typography,
  Tooltip,
  FormControl,
  FormHelperText,
  InputLabel,
  List,
  ListItem,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  useTheme,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  DateRangePicker,
  LocalizationProvider,
  MultiInputDateRangeField,
  PickersShortcutsProps,
} from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { parseISO } from "date-fns";
import { gql } from "generated-graphql";
import { RegulatoryStatusOption } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useEffect, useMemo, useState, useCallback } from "react";
import {
  useWasteOverviewState,
  wasteOverviewDefaultFilters,
} from "./WasteOverviewContext";
import {
  ALL_GENERATOR_STATUS_OPTIONS,
  GENERATOR_STATUS_OPTIONS,
  US_STATES,
} from "./types";
import UnfoldLess from "@mui/icons-material/UnfoldLess";

const TAGS_QUERY = gql(/* GraphQL */ `
  query FacilityTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

const WASTE_LINE_TAGS_QUERY = gql(/* GraphQL */ `
  query WasteLineTags(
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tags(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

export function OverviewGlobalFilters() {
  const {
    overviewState: { filters: urlFilters },
    setOverviewState,
  } = useWasteOverviewState();
  const theme = useTheme();
  const { tenantId } = useTenant();

  const [tagsMenuAnchor, setTagsMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [facilityTagsExpanded, setFacilityTagsExpanded] = useState(true);
  const [lineItemTagsExpanded, setLineItemTagsExpanded] = useState(true);

  const [intermediateFilters, setIntermediateFilters] =
    useState<typeof urlFilters>(urlFilters);

  // State to hold accumulated tags across pages
  const [allFacilityTags, setAllFacilityTags] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [allWasteLineTags, setAllWasteLineTags] = useState<
    Array<{ id: string; name: string }>
  >([]);

  // Pagination state
  const [facilityTagsPage, setFacilityTagsPage] = useState(0);
  const [wasteLineTagsPage, setWasteLineTagsPage] = useState(0);
  const [isFetchingMoreFacilityTags, setIsFetchingMoreFacilityTags] =
    useState(false);
  const [isFetchingMoreWasteLineTags, setIsFetchingMoreWasteLineTags] =
    useState(false);

  const { data: tagsData } = useSuspenseQuery(TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} type:Facility`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !tenantId,
  });

  const { data: wasteLineTagsData } = useSuspenseQuery(WASTE_LINE_TAGS_QUERY, {
    variables: {
      search: `tenantId:${tenantId} type:Waste`,
      page: 0,
      pageSize: 100,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !tenantId,
  });

  // Lazy query for loading more facility tags
  const [fetchMoreFacilityTags] = useLazyQuery(TAGS_QUERY, {
    onCompleted: (data) => {
      if (data?.tags?.items) {
        setAllFacilityTags((prev) => [...prev, ...data.tags.items]);
      }
      setIsFetchingMoreFacilityTags(false);
    },
    onError: () => {
      setIsFetchingMoreFacilityTags(false);
    },
  });

  // Lazy query for loading more waste line tags
  const [fetchMoreWasteLineTags] = useLazyQuery(WASTE_LINE_TAGS_QUERY, {
    onCompleted: (data) => {
      if (data?.tags?.items) {
        setAllWasteLineTags((prev) => [...prev, ...data.tags.items]);
      }
      setIsFetchingMoreWasteLineTags(false);
    },
    onError: () => {
      setIsFetchingMoreWasteLineTags(false);
    },
  });

  // Initialize tag lists with initial data
  useEffect(() => {
    if (tagsData?.tags?.items) {
      setAllFacilityTags(tagsData.tags.items);
    }
  }, [tagsData]);

  useEffect(() => {
    if (wasteLineTagsData?.tags?.items) {
      setAllWasteLineTags(wasteLineTagsData.tags.items);
    }
  }, [wasteLineTagsData]);

  // Reset accumulated tags and pagination when menu is opened
  useEffect(() => {
    if (tagsMenuAnchor) {
      // Reset to initial data when menu opens
      if (tagsData?.tags?.items) {
        setAllFacilityTags(tagsData.tags.items);
      }
      if (wasteLineTagsData?.tags?.items) {
        setAllWasteLineTags(wasteLineTagsData.tags.items);
      }
      setFacilityTagsPage(0);
      setWasteLineTagsPage(0);
    }
  }, [tagsMenuAnchor, tagsData, wasteLineTagsData]);

  useEffect(() => {
    setIntermediateFilters(urlFilters);
  }, [urlFilters]);

  // Calculate if there are more tags to load
  const hasMoreFacilityTags = useMemo(() => {
    if (!tagsData?.tags?.count) return false;
    return allFacilityTags.length < tagsData.tags.count;
  }, [tagsData?.tags?.count, allFacilityTags.length]);

  const hasMoreWasteLineTags = useMemo(() => {
    if (!wasteLineTagsData?.tags?.count) return false;
    return allWasteLineTags.length < wasteLineTagsData.tags.count;
  }, [wasteLineTagsData?.tags?.count, allWasteLineTags.length]);

  // Scroll handler for facility tags
  const handleFacilityTagsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollThreshold = 50;
      const scrollDistance =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (
        scrollDistance > scrollThreshold &&
        hasMoreFacilityTags &&
        !isFetchingMoreFacilityTags
      ) {
        setIsFetchingMoreFacilityTags(true);
        const nextPage = facilityTagsPage + 1;
        setFacilityTagsPage(nextPage);

        fetchMoreFacilityTags({
          variables: {
            search: `tenantId:${tenantId} type:Facility`,
            page: nextPage,
            pageSize: 100,
            sort: [{ field: "name", sort: "asc" }],
          },
        });
      }
    },
    [
      hasMoreFacilityTags,
      isFetchingMoreFacilityTags,
      facilityTagsPage,
      tenantId,
      fetchMoreFacilityTags,
    ]
  );

  // Scroll handler for waste line tags
  const handleWasteLineTagsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const scrollThreshold = 50;
      const scrollDistance =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (
        scrollDistance > scrollThreshold &&
        hasMoreWasteLineTags &&
        !isFetchingMoreWasteLineTags
      ) {
        setIsFetchingMoreWasteLineTags(true);
        const nextPage = wasteLineTagsPage + 1;
        setWasteLineTagsPage(nextPage);

        fetchMoreWasteLineTags({
          variables: {
            search: `tenantId:${tenantId} type:Waste`,
            page: nextPage,
            pageSize: 100,
            sort: [{ field: "name", sort: "asc" }],
          },
        });
      }
    },
    [
      hasMoreWasteLineTags,
      isFetchingMoreWasteLineTags,
      wasteLineTagsPage,
      tenantId,
      fetchMoreWasteLineTags,
    ]
  );

  const filters = useMemo(() => {
    if (Object.keys(intermediateFilters).length === 0) {
      return wasteOverviewDefaultFilters;
    } else {
      return intermediateFilters;
    }
  }, [intermediateFilters]);

  // Use optimistic filters for immediate UI feedback
  const {
    shipDateStart: startDate,
    shipDateEnd: endDate,
    currentGeneratorStatuses: generatorStatus,
    states,
    facilityTagIds,
    wasteLineTagIds,
  } = filters;
  const totalSelectedTags = useMemo(() => {
    return (facilityTagIds?.length ?? 0) + (wasteLineTagIds?.length ?? 0);
  }, [facilityTagIds, wasteLineTagIds]);

  // Generator Status handlers
  const allSelected =
    GENERATOR_STATUS_OPTIONS.length > 0 &&
    generatorStatus?.length === GENERATOR_STATUS_OPTIONS.length;
  const someSelected =
    generatorStatus &&
    generatorStatus.length > 0 &&
    generatorStatus.length < GENERATOR_STATUS_OPTIONS.length;

  const generatorStatusHelperNeeded =
    !generatorStatus || generatorStatus.length === 0;

  const statesHelperNeeded = !states || states.length === 0;

  // Handlers
  const handleDateRangeChange = (newValue: [Date | null, Date | null]) => {
    const toIsoString = (d: Date | null): string | undefined => {
      if (!d) return undefined;
      if (isNaN(d.getTime())) return undefined;
      try {
        return d.toISOString().slice(0, 10);
      } catch {
        return undefined;
      }
    };

    setIntermediateFilters((f) => ({
      ...f,
      shipDateStart: toIsoString(newValue[0]),
      shipDateEnd: toIsoString(newValue[1]),
    }));
    setOverviewState((f) => {
      f.shipDateStart = toIsoString(newValue[0]);
      f.shipDateEnd = toIsoString(newValue[1]);
    });
  };

  // Helper text logic for date range
  const safeParseDate = (iso?: string): Date | null => {
    if (!iso) return null;
    const d = parseISO(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  const start = safeParseDate(startDate);
  const end = safeParseDate(endDate);

  let dateHelperText: string | null = null;

  if (!start || !end) {
    dateHelperText = "Select a start and end date to view data";
  }

  const handleGeneratorStatusChange = (e: SelectChangeEvent<string[]>) => {
    const raw = e.target.value as (RegulatoryStatusOption | "select-all")[];

    // Did the user click the "select-all" row?
    if (raw.includes("select-all")) {
      const isEverythingSelected =
        generatorStatus?.length === GENERATOR_STATUS_OPTIONS.length;

      setIntermediateFilters((f) => ({
        ...f,
        currentGeneratorStatuses: isEverythingSelected
          ? []
          : [...ALL_GENERATOR_STATUS_OPTIONS],
      }));

      return;
    }

    // Normal multi-select path  – strip out accidental sentinel value
    const cleaned = raw.filter((v) => v !== "select-all");

    setIntermediateFilters((f) => ({
      ...f,
      currentGeneratorStatuses: cleaned,
    }));
  };

  const handleStatesChange = (e: any) => {
    const raw = e.target.value as string[];

    if (raw.includes("select-all")) {
      const isEverythingSelected = states?.length === US_STATES.length;
      setIntermediateFilters((f) => ({
        ...f,
        states: isEverythingSelected ? [] : [...US_STATES],
      }));

      return;
    }

    const cleaned = raw.filter((v) => v !== "select-all");
    setIntermediateFilters((f) => ({
      ...f,
      states: cleaned,
    }));
  };

  const standardInputProps = {
    size: "small" as const,
    fullWidth: true,
    sx: {
      "& .MuiInputBase-root": {
        height: "40px", // Standardize height across all inputs
      },
    },
  };

  return (
    <SubNavAppBar
      sx={{
        top: {
          xs: theme.spacing(xsHeight + 6.1),
          md: theme.spacing(mdHeight + 6.1),
        },
        pt: theme.spacing(2),
        pl: theme.spacing(3),
        pb: theme.spacing(2),
        borderBottom: "1px solid",
        borderColor: theme.palette.divider,
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems="start"
        spacing={2}
      >
        {/* Generator Status */}
        <FormControl
          sx={{ ...standardInputProps.sx, width: { xs: "100%", md: 210 } }}
          variant="standard"
        >
          <InputLabel>Current Generator Status</InputLabel>
          <Select
            open={generatorOpen}
            onOpen={() => setGeneratorOpen(true)}
            onClose={() => {
              setGeneratorOpen(false);
              setOverviewState((f) => {
                f.currentGeneratorStatuses =
                  intermediateFilters.currentGeneratorStatuses;
              });
            }}
            multiple
            label="Current Generator Status"
            value={generatorStatus ?? []}
            onChange={handleGeneratorStatusChange}
            renderValue={(selected) => {
              const displayValue = selected.map(
                (status) =>
                  GENERATOR_STATUS_OPTIONS.find(
                    (option) => option.value === status
                  )?.label
              );
              return selected.length === GENERATOR_STATUS_OPTIONS.length
                ? `All (${selected.length})`
                : displayValue.join(", ");
            }}
            variant="standard"
          >
            <MenuItem
              value="select-all"
              onClick={() => {
                const isEverythingSelected =
                  generatorStatus?.length === GENERATOR_STATUS_OPTIONS.length;

                const newStatusFilters = isEverythingSelected
                  ? []
                  : [...GENERATOR_STATUS_OPTIONS.map((option) => option.value)];

                setIntermediateFilters((f) => ({
                  ...f,
                  currentGeneratorStatuses: newStatusFilters,
                }));

                if (!isEverythingSelected) {
                  setOverviewState((f) => {
                    f.currentGeneratorStatuses = newStatusFilters;
                  });
                }
              }}
            >
              <Checkbox checked={allSelected} indeterminate={someSelected} />
              Select all
            </MenuItem>
            {GENERATOR_STATUS_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={!!generatorStatus?.includes(option.value)} />
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {generatorStatusHelperNeeded && (
            <FormHelperText sx={{ whiteSpace: "normal" }}>
              Select at least one generator status to view data
            </FormHelperText>
          )}
        </FormControl>

        {/* State */}
        <FormControl
          sx={{ ...standardInputProps.sx, width: { xs: "100%", md: 132 } }}
          variant="standard"
        >
          <InputLabel>State</InputLabel>
          <Select
            open={stateOpen}
            onOpen={() => setStateOpen(true)}
            onClose={() => {
              setStateOpen(false);
              setOverviewState((f) => {
                f.states = intermediateFilters.states;
              });
            }}
            multiple
            value={states ?? []}
            onChange={handleStatesChange}
            label="State"
            renderValue={(selected) =>
              selected.length === US_STATES.length
                ? `All (${selected.length})`
                : selected.length > 3
                ? `${selected.length} selected`
                : (selected as string[]).join(", ")
            }
            MenuProps={{
              MenuListProps: {
                // Only scroll to the selected item if select-all is not selected
                autoFocusItem:
                  states?.length === US_STATES.length ? false : true,
              },
            }}
          >
            <MenuItem
              value="select-all"
              onClick={() => {
                const isEverythingSelected =
                  states?.length === US_STATES.length;
                setIntermediateFilters((f) => ({
                  ...f,
                  states: isEverythingSelected ? [] : [...US_STATES],
                }));
              }}
            >
              <Checkbox
                checked={states?.length === US_STATES.length}
                indeterminate={
                  (states?.length ?? 0) > 0 &&
                  (states?.length ?? 0) < US_STATES.length
                }
              />
              Select All
            </MenuItem>
            {US_STATES.map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={!!states?.includes(option)} />
                {option}
              </MenuItem>
            ))}
          </Select>
          {statesHelperNeeded && (
            <FormHelperText sx={{ whiteSpace: "normal" }}>
              Select at least one state to view data
            </FormHelperText>
          )}
        </FormControl>

        {/* Date Range Picker */}
        <FormControl sx={{ minWidth: 300 }} size="small" variant="standard">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateRangePicker
              calendars={2}
              onAccept={handleDateRangeChange}
              value={[start, end]}
              localeText={{
                start: "Ship Date Start",
                end: "Ship Date End",
              }}
              slots={{
                field: MultiInputDateRangeField,
                shortcuts: HorizontalShortcuts,
              }}
              slotProps={{
                textField: {
                  ...standardInputProps,
                  fullWidth: true,
                  variant: "standard",
                  size: "small",
                  inputProps: {
                    autoComplete: "off",
                    "data-1p-ignore": "true",
                    "data-lpignore": "true",
                    "data-form-type": "other",
                  },
                  InputProps: {
                    endAdornment: <InsertInvitationIcon />,
                  },
                },
                shortcuts: { items: datePickerShortcut },
              }}
            />
          </LocalizationProvider>
          {dateHelperText && <FormHelperText>{dateHelperText}</FormHelperText>}
        </FormControl>

        {/* Combined Tags (Facility & Waste Line) */}
        <Box sx={{ alignItems: "end" }}>
          <Chip
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>
                  {totalSelectedTags > 0
                    ? `Tags (${totalSelectedTags})`
                    : "Tags"}
                </span>
                <ExpandMoreIcon fontSize="small" />
              </Stack>
            }
            color={tagsMenuAnchor ? "primary" : "default"}
            variant="filled"
            sx={{
              ...standardInputProps.sx,
              mt: { xs: 0, md: "20px" },
            }}
            onClick={(e) => setTagsMenuAnchor(e.currentTarget)}
            clickable
            size="medium"
          />
          <Menu
            anchorEl={tagsMenuAnchor}
            open={Boolean(tagsMenuAnchor)}
            onClose={() => {
              setTagsMenuAnchor(null);
              // Reset pagination state when menu closes
              setFacilityTagsPage(0);
              setWasteLineTagsPage(0);
              setOverviewState((f) => {
                f.facilityTagIds = intermediateFilters.facilityTagIds;
                f.wasteLineTagIds = intermediateFilters.wasteLineTagIds;
              });
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{
              mt: 0.5,
            }}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: "70vh",
                  py: 0,
                },
              },
              list: {
                sx: {
                  p: 0,
                },
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", width: 200 }}>
              {/* Tag Title */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 2,
                  py: 2,
                }}
              >
                <Typography variant="subtitle2">Tags</Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setFacilityTagsExpanded(false);
                    setLineItemTagsExpanded(false);
                  }}
                  sx={{
                    visibility:
                      facilityTagsExpanded || lineItemTagsExpanded
                        ? "visible"
                        : "hidden",
                  }}
                >
                  <UnfoldLess />
                </IconButton>
              </Box>
              <Divider
                orientation="horizontal"
                sx={{ width: "100%" }}
                flexItem
              />
              {/* Facility Tags */}
              <Box
                sx={{
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => setFacilityTagsExpanded(!facilityTagsExpanded)}
                >
                  <Typography variant="subtitle2">Facility Tags</Typography>
                  <IconButton size="small">
                    {facilityTagsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>
                {facilityTagsExpanded && (
                  <Box
                    sx={{
                      maxHeight: 250,
                      px: 1,
                      overflowY: "auto",
                    }}
                    onScroll={handleFacilityTagsScroll}
                  >
                    {allFacilityTags.length === 0 && (
                      <MenuItem disabled={true}>No tags available</MenuItem>
                    )}
                    {allFacilityTags.length > 0 && (
                      <MenuItem
                        value="select-all"
                        sx={{
                          p: 0,
                        }}
                        onClick={() => {
                          const isAllSelected =
                            (facilityTagIds?.length ?? 0) ===
                            allFacilityTags.length;
                          setIntermediateFilters((f) => ({
                            ...f,
                            facilityTagIds: isAllSelected
                              ? []
                              : allFacilityTags.map((tag) => tag.id),
                          }));
                        }}
                      >
                        <Checkbox
                          checked={
                            facilityTagIds?.length === allFacilityTags.length
                          }
                          indeterminate={
                            (facilityTagIds?.length ?? 0) > 0 &&
                            (facilityTagIds?.length ?? 0) <
                              allFacilityTags.length
                          }
                        />
                        Select All
                      </MenuItem>
                    )}
                    {allFacilityTags.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{
                          p: 0,
                        }}
                        onClick={() => {
                          const isSelected = facilityTagIds?.includes(
                            option.id
                          );
                          setIntermediateFilters((f) => ({
                            ...f,
                            facilityTagIds: isSelected
                              ? facilityTagIds?.filter((id) => id !== option.id)
                              : [...(facilityTagIds ?? []), option.id],
                          }));
                        }}
                      >
                        <Checkbox
                          checked={!!facilityTagIds?.includes(option.id)}
                        />
                        <Tooltip title={option.name} placement="right">
                          <Typography
                            noWrap
                            sx={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {option.name}
                          </Typography>
                        </Tooltip>
                      </MenuItem>
                    ))}
                    {isFetchingMoreFacilityTags && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Divider
                orientation="horizontal"
                sx={{ width: "100%" }}
                flexItem
              />
              {/* Waste Tags Column */}
              <Box
                sx={{
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2,
                    cursor: "pointer",
                  }}
                  onClick={() => setLineItemTagsExpanded(!lineItemTagsExpanded)}
                >
                  <Typography variant="subtitle2">Line Item Tags</Typography>
                  <IconButton size="small">
                    {lineItemTagsExpanded ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </Box>
                {lineItemTagsExpanded && (
                  <Box
                    sx={{
                      maxHeight: 250,
                      px: 1,
                      overflowY: "auto",
                    }}
                    onScroll={handleWasteLineTagsScroll}
                  >
                    {allWasteLineTags.length === 0 && (
                      <MenuItem disabled={true}>No tags available</MenuItem>
                    )}
                    {allWasteLineTags.length > 0 && (
                      <MenuItem
                        value="select-all"
                        sx={{
                          p: 0,
                        }}
                        onClick={() => {
                          const isAllSelected =
                            (wasteLineTagIds?.length ?? 0) ===
                            allWasteLineTags.length;
                          setIntermediateFilters((f) => ({
                            ...f,
                            wasteLineTagIds: isAllSelected
                              ? []
                              : allWasteLineTags.map((tag) => tag.id),
                          }));
                        }}
                      >
                        <Checkbox
                          checked={
                            wasteLineTagIds?.length === allWasteLineTags.length
                          }
                          indeterminate={
                            (wasteLineTagIds?.length ?? 0) > 0 &&
                            (wasteLineTagIds?.length ?? 0) <
                              allWasteLineTags.length
                          }
                        />
                        Select All
                      </MenuItem>
                    )}
                    {allWasteLineTags.map((option) => (
                      <MenuItem
                        key={option.id}
                        value={option.id}
                        sx={{
                          p: 0,
                        }}
                        onClick={() => {
                          const isSelected = wasteLineTagIds?.includes(
                            option.id
                          );
                          setIntermediateFilters((f) => ({
                            ...f,
                            wasteLineTagIds: isSelected
                              ? wasteLineTagIds?.filter(
                                  (id) => id !== option.id
                                )
                              : [...(wasteLineTagIds ?? []), option.id],
                          }));
                        }}
                      >
                        <Checkbox
                          checked={!!wasteLineTagIds?.includes(option.id)}
                        />
                        <Tooltip title={option.name} placement="right">
                          <Typography
                            noWrap
                            sx={{
                              ml: 1,
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {option.name}
                          </Typography>
                        </Tooltip>
                      </MenuItem>
                    ))}
                    {isFetchingMoreWasteLineTags && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          py: 1,
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Menu>
        </Box>
      </Stack>
    </SubNavAppBar>
  );
}

const datePickerShortcut = [
  {
    label: `${new Date().getFullYear()}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), 0, 1);
      const end = new Date(today.getFullYear(), 11, 31);
      return [start, end];
    },
  },
  {
    label: `${new Date().getFullYear() - 1}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 1, 0, 1);
      const end = new Date(today.getFullYear() - 1, 11, 31);
      return [start, end];
    },
  },
  {
    label: `${new Date().getFullYear() - 2}`,
    getValue: () => {
      const today = new Date();
      const start = new Date(today.getFullYear() - 2, 0, 1);
      const end = new Date(today.getFullYear() - 2, 11, 31);
      return [start, end];
    },
  },
  {
    label: "Last Quarter",
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
      const lastQuarterYear =
        currentQuarter === 0 ? today.getFullYear() - 1 : today.getFullYear();
      const start = new Date(lastQuarterYear, lastQuarter * 3, 1);
      const end = new Date(lastQuarterYear, (lastQuarter + 1) * 3, 0);
      return [start, end];
    },
  },
  {
    label: "This Quarter",
    getValue: () => {
      const today = new Date();
      const currentQuarter = Math.floor(today.getMonth() / 3);
      const start = new Date(today.getFullYear(), currentQuarter * 3, 1);
      const end = new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0);
      return [start, end];
    },
  },
  {
    label: "Reset",
    getValue: () => [null, null],
  },
];

function HorizontalShortcuts(props: PickersShortcutsProps<DateRange<Date>>) {
  const { items, onChange, isValid, changeImportance = "accept" } = props;

  if (items == null || items.length === 0) {
    return null;
  }

  const resolvedItems = items.map((item) => {
    const newValue = item.getValue({ isValid });

    return {
      label: item.label,
      onClick: () => {
        onChange(newValue, changeImportance, item);
      },
      disabled: !isValid(newValue),
    };
  });

  return (
    <Box
      sx={{
        width: "100%",
        gridRow: 1,
        gridColumn: 2,
      }}
    >
      <List
        dense
        sx={(theme) => ({
          display: "flex",
          justifyContent: "center",
          px: theme.spacing(2),
          "& .MuiListItem-root": {
            width: "inherit",
            py: 0,
            px: theme.spacing(0.5),
          },
        })}
      >
        {resolvedItems.map((item) => {
          return (
            <ListItem key={item.label}>
              <Chip {...item} />
            </ListItem>
          );
        })}
      </List>
      <Divider />
    </Box>
  );
}
